import flowsApi from './flowsApiService';

const LOCAL_API_BASE =
  (import.meta.env?.VITE_AGENT_LOCAL_API_BASE?.trim?.() ||
    'http://127.0.0.1:3000').replace(/\/+$/, '');

let connectivityState = {
  state: 'ONLINE',
  mode: 'AUTO',
  reason: 'assumed_online',
};

const cloudExecutor = {
  name: 'cloud',
  async execute(commandId, payload) {
    console.info('[CommandExecutor] execute (cloud)', {
      commandId,
      mode: connectivityState.mode,
      state: connectivityState.state,
    });
    // Preserve existing behavior: flowsApi.execute expects { flowId, tenantId }
    return flowsApi.execute(payload);
  },
};

const localExecutor = {
  name: 'local',
  async execute(commandId, payload) {
    const type = (payload?.type || '').toString().toLowerCase();
    const body = {
      commandId,
      type,
      payload,
    };
    console.info('[CommandExecutor] execute (local)', {
      commandId,
      mode: connectivityState.mode,
      state: connectivityState.state,
      type,
    });
    if (!type) {
      throw new Error('Local command type is required (e.g., start_rtp_paging or stop_rtp_paging)');
    }
    const resp = await fetch(`${LOCAL_API_BASE}/api/local/command`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      data = await resp.json();
    } catch (_) {}
    if (!resp.ok || data?.success === false) {
      const msg = (data && data.error) || `Local command failed with status ${resp.status}`;
      console.error('[CommandExecutor] Local command failed', { commandId, type, status: resp.status, error: msg });
      throw new Error(msg);
    }
    console.info('[CommandExecutor] Local command success', { commandId, type, result: data });
    return data;
  },
};

function selectExecutor(state, payload) {
  const offline = (state?.state || '').toUpperCase() === 'OFFLINE';
  const hasLocalType = !!(payload && payload.type);
  if (offline && hasLocalType) return localExecutor;
  return cloudExecutor;
}

async function refreshConnectivity() {
  try {
    const api = (typeof window !== 'undefined' && window.electronAPI) ? window.electronAPI : null;
    if (!api || typeof api.getConnectivityState !== 'function') return connectivityState;
    const next = await api.getConnectivityState();
    connectivityState = {
      state: (next?.state || connectivityState.state || 'UNKNOWN').toUpperCase(),
      mode: (next?.mode || connectivityState.mode || 'AUTO').toUpperCase(),
      reason: next?.reason || connectivityState.reason || null,
    };
    return connectivityState;
  } catch (e) {
    console.warn('[CommandExecutor] Failed to refresh connectivity state', e);
    return connectivityState;
  }
}

function subscribeToConnectivity() {
  try {
    const api = (typeof window !== 'undefined' && window.electronAPI) ? window.electronAPI : null;
    if (!api || typeof api.onConnectivityChange !== 'function') return;
    api.onConnectivityChange((payload) => {
      connectivityState = {
        state: (payload?.state || connectivityState.state || 'UNKNOWN').toUpperCase(),
        mode: (payload?.mode || connectivityState.mode || 'AUTO').toUpperCase(),
        reason: payload?.reason || connectivityState.reason || null,
      };
      console.info('[CommandExecutor] Connectivity update', connectivityState);
    });
  } catch (e) {
    console.warn('[CommandExecutor] Failed to subscribe to connectivity updates', e);
  }
}

subscribeToConnectivity();

export async function execute(commandId, payload) {
  await refreshConnectivity();
  
  // Block command execution during transition states
  const stateUpper = (connectivityState?.state || '').toUpperCase();
  if (stateUpper === 'RECOVERING') {
    throw new Error('System is recovering connectivity. Please wait and try again.');
  }
  
  const executor = selectExecutor(connectivityState, payload);
  return executor.execute(commandId, payload);
}

export function getConnectivityState() {
  return { ...connectivityState };
}
